import { createContext, createElement, useContext, useCallback, useState, useEffect } from 'react';

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

/**
 * The basic state context where we will carry either a Storage, or a state
 * for the current nesting.
 */
var StateContext = /*#__PURE__*/createContext(null);
var InstanceProvider = StateContext.Provider;
/**
 * Provider for the Storage-instance to be used in all child-components.
 */

var StorageProvider = function StorageProvider(_ref) {
  var storage = _ref.storage,
      children = _ref.children;
  return /*#__PURE__*/createElement(InstanceProvider, {
    value: storage
  }, children);
};
/**
 * Returns a function for passing messages into the state-tree at the current
 * nesting.
 */

function useSendMessage() {
  var supervisor = useContext(StateContext);

  if (!supervisor) {
    throw new Error("useSendMessage() must be used inside a <State.Provider />.");
  }

  return useCallback(function (message, sourceName) {
    return supervisor.sendMessage(message, sourceName);
  }, [supervisor]);
}
/**
 * Exclude children and name properties when using createState, children
 * are always new objects and are most likely not of interest to the state, and
 * name is an external parameter.
 */

function excludeChildren(props) {
  // Manually implemented object-rest-spread to avoid Babel's larger
  // implementation
  // Object.assign causes Babel to to add an unnecessary polyfill so use spread
  var rest = _extends({}, props);

  delete rest.children;
  delete rest.name;
  return rest;
}

function createStateData(model) {
  var Ctx = /*#__PURE__*/createContext(undefined);
  var Provider = Ctx.Provider;

  var DataProvider = function DataProvider(props) {
    var context = useContext(StateContext);

    if (!context) {
      throw new Error("<" + model.id + ".Provider /> must be used inside a <StorageProvider />");
    }

    var name = props.name;
    var instance = context.getState(model, name) || context.createState(model, excludeChildren(props), name);

    var _useState = useState(instance.getData()),
        data = _useState[0],
        setData = _useState[1];

    useEffect(function () {
      instance.addListener("stateNewData", setData); // Data can be new since we are runnning componentDidMount()
      // after render()

      var newData = instance.getData();

      if (data !== newData) {
        // Force re-render immediately
        setData(newData);
      }

      return function () {
        instance.removeListener("stateNewData", setData); // Drop the state if we were the last listener

        if (instance.listeners("stateNewData").length === 0) {
          context.removeState(model, name);
        }
      }; // We need to skip the dep on data since otherwise we are going to
      // re-register the state every time

      /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, [context, instance]);
    return /*#__PURE__*/createElement(InstanceProvider, {
      value: instance
    }, /*#__PURE__*/createElement(Provider, {
      value: data
    }, props.children));
  };

  return {
    _dataContext: Ctx,
    model: model,
    // We have to cheat here since the value must be possible to use as
    // undefined internally, but when testing it should not be possible to use
    // without a fully defined `T`:
    TestProvider: Provider,
    Provider: DataProvider,
    Consumer: Ctx.Consumer
  };
}
/**
 * Returns the data in the topmost state associated with the supplied
 * StateData. Will throw if a StateData.Provider is not a parent node.
 */

function useData(context) {
  var _dataContext = context._dataContext,
      model = context.model;
  var data = useContext(_dataContext);

  if (data === undefined) {
    throw new Error("useData(" + model.id + ") must be used inside a <" + model.id + ".Provider />");
  }

  return data;
}

export { StateContext, StorageProvider, createStateData, useData, useSendMessage };
//# sourceMappingURL=index.js.map
